import { queryOptions } from '@tanstack/react-query';
import {
  offers,
  mediaUnits,
  campaignSale,
  offerApproval,
  offerVersionEndpoints,
} from '@ooh/contracts';
import type { CampaignNumberPreviewQuery, EndpointDef, callEndpoint } from '@ooh/contracts';
import {
  offerAvailabilityQueryKey,
  offerDetailQueryKey,
  offerVersionsQueryKey,
  offerVersionsRequest,
} from '@ooh/screens';

export interface WithCallWithAuth {
  callWithAuth: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
  ) => ReturnType<typeof callEndpoint<TDef>>;
}

export const offerKeys = {
  detail: offerDetailQueryKey,
  unitClientDetail: (offerId: string, unitId: string) =>
    ['offer-unit-client-detail', offerId, unitId] as const,
} as const;

export function listVersionsOptions(auth: WithCallWithAuth, offerId: string) {
  return queryOptions({
    queryKey: offerVersionsQueryKey(offerId),
    queryFn: () => auth.callWithAuth(offerVersionEndpoints.list, offerVersionsRequest(offerId)),
  });
}

export const offerAssigneeKeys = {
  all: ['offer-assignable-users'] as const,
} as const;

export const offerSalesUserKeys = {
  all: ['offer-sales-users'] as const,
} as const;

// offer-draft-numbering / "Campaign-number preview on the offer creation
// form". The key embeds the full query object rather than individual
// fields — a small extra query-key surface is a reasonable trade for not
// hand-maintaining a positional-args key shape for a query with 4 inputs.
export const campaignNumberPreviewKeys = {
  preview: (query: CampaignNumberPreviewQuery) => ['offer-campaign-number-preview', query] as const,
} as const;

export const offerAvailabilityKeys = {
  availability: offerAvailabilityQueryKey,
} as const;

export const offerPublicLinkKeys = {
  all: ['offer-public-link'] as const,
  status: (offerId: string) => [...offerPublicLinkKeys.all, 'status', offerId] as const,
} as const;

// campaign-sale / "Sale preview" — powers the record-sale dialog's breakdown.
export const salePreviewKeys = {
  detail: (offerId: string) => ['offer-sale-preview', offerId] as const,
} as const;

// offer-approval / "Campaign confirmation preview" — powers the confirm-campaign
// dialog's pre-commit breakdown (action-breakdown).
export const confirmationPreviewKeys = {
  detail: (offerId: string) => ['offer-confirmation-preview', offerId] as const,
} as const;

// offers.list is paginated (CU-86cb4wjwk) — use useOffersList from @ooh/screens, which drains
// every page, instead of adding a `list` entry here that would only surface page one.
export const offerQueries = {
  assignableUsers: (auth: WithCallWithAuth) =>
    queryOptions({
      queryKey: offerAssigneeKeys.all,
      queryFn: () => auth.callWithAuth(offers.listAssignableUsers, {}),
    }),
  salesUsers: (auth: WithCallWithAuth) =>
    queryOptions({
      queryKey: offerSalesUserKeys.all,
      queryFn: () => auth.callWithAuth(offers.listSalesUsers, {}),
    }),
  campaignNumberPreview: (auth: WithCallWithAuth, query: CampaignNumberPreviewQuery) =>
    queryOptions({
      queryKey: campaignNumberPreviewKeys.preview(query),
      queryFn: () => auth.callWithAuth(offers.previewCampaignNumber, query),
    }),
  detail: (auth: WithCallWithAuth, offerId: string) =>
    queryOptions({
      queryKey: offerKeys.detail(offerId),
      queryFn: () => auth.callWithAuth(offers.getById, { id: Number(offerId) }),
    }),
  unitClientDetail: (auth: WithCallWithAuth, offerId: string, unitId: string) =>
    queryOptions({
      queryKey: offerKeys.unitClientDetail(offerId, unitId),
      queryFn: () =>
        auth.callWithAuth(mediaUnits.getClientDetail, {
          offerId: Number(offerId),
          unitId: Number(unitId),
        }),
    }),
  salePreview: (auth: WithCallWithAuth, offerId: string) =>
    queryOptions({
      queryKey: salePreviewKeys.detail(offerId),
      queryFn: () => auth.callWithAuth(campaignSale.salePreview, { id: Number(offerId) }),
    }),
  confirmationPreview: (auth: WithCallWithAuth, offerId: string) =>
    queryOptions({
      queryKey: confirmationPreviewKeys.detail(offerId),
      queryFn: () => auth.callWithAuth(offerApproval.confirmationPreview, { id: Number(offerId) }),
    }),
};
